<template>
  <div class="turnstile-container">
    <div ref="turnstileElement" class="turnstile-widget"></div>
    <div v-if="error" class="turnstile-error">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

// Props
const props = defineProps({
  siteKey: {
    type: String,
    required: true
  },
  theme: {
    type: String,
    default: 'light' // 'light' or 'dark'
  },
  size: {
    type: String,
    default: 'normal' // 'normal' or 'compact'
  },
  tabindex: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['success', 'error', 'expired', 'timeout'])

// Refs
const turnstileElement = ref(null)
const widgetId = ref(null)
const error = ref('')

// 加载Turnstile脚本
const loadTurnstileScript = () => {
  return new Promise((resolve, reject) => {
    // 检查是否已经加载
    if (window.turnstile) {
      resolve()
      return
    }

    // 检查是否正在加载
    if (window.turnstileLoading) {
      window.turnstileLoading.then(resolve).catch(reject)
      return
    }

    // 创建加载Promise
    window.turnstileLoading = new Promise((scriptResolve, scriptReject) => {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      
      script.onload = () => {
        window.turnstileLoading = null
        scriptResolve()
      }
      
      script.onerror = () => {
        window.turnstileLoading = null
        scriptReject(new Error('Failed to load Turnstile script'))
      }
      
      document.head.appendChild(script)
    })

    window.turnstileLoading.then(resolve).catch(reject)
  })
}

// 渲染Turnstile组件
const renderTurnstile = () => {
  if (!window.turnstile || !turnstileElement.value) {
    return
  }

  try {
    // 清除之前的widget
    if (widgetId.value) {
      window.turnstile.remove(widgetId.value)
    }

    // 渲染新的widget
    widgetId.value = window.turnstile.render(turnstileElement.value, {
      sitekey: props.siteKey,
      theme: props.theme,
      size: props.size,
      tabindex: props.tabindex,
      callback: (token) => {
        emit('success', token)
        error.value = ''
      },
      'error-callback': (errorCode) => {
        emit('error', errorCode)
        // 不在这里显示错误，由父组件处理
      },
      'expired-callback': () => {
        emit('expired')
        // 不在这里显示错误，由父组件处理
      },
      'timeout-callback': () => {
        emit('timeout')
        // 不在这里显示错误，由父组件处理
      }
    })
  } catch (err) {
    console.error('Turnstile render error:', err)
    error.value = '验证组件加载失败'
    emit('error', err)
  }
}

// 获取错误信息
const getErrorMessage = (errorCode) => {
  const errorMessages = {
    'network-error': '网络错误，请检查网络连接',
    'config-error': '配置错误',
    'rate-limited': '请求过于频繁，请稍后重试',
    'invalid-sitekey': '无效的站点密钥',
    'invalid-domain': '无效的域名',
    'timeout-or-duplicate': '验证超时或重复提交'
  }
  return errorMessages[errorCode] || '验证失败，请重试'
}

// 重置Turnstile
const reset = () => {
  if (window.turnstile && widgetId.value) {
    window.turnstile.reset(widgetId.value)
    error.value = ''
  }
}

// 获取当前token
const getResponse = () => {
  if (window.turnstile && widgetId.value) {
    return window.turnstile.getResponse(widgetId.value)
  }
  return ''
}

// 暴露方法给父组件
defineExpose({
  reset,
  getResponse
})

// 监听props变化
watch(() => props.siteKey, () => {
  if (window.turnstile) {
    renderTurnstile()
  }
})

// 生命周期
onMounted(async () => {
  try {
    await loadTurnstileScript()
    renderTurnstile()
  } catch (err) {
    console.error('Failed to load Turnstile:', err)
    error.value = '验证组件加载失败'
  }
})

onUnmounted(() => {
  if (window.turnstile && widgetId.value) {
    window.turnstile.remove(widgetId.value)
  }
})
</script>

<style scoped>
.turnstile-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem 0;
}

.turnstile-widget {
  width: 100%;
  display: flex;
  justify-content: center;
}

.turnstile-error {
  color: #ff6b6b;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 0.5rem;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  width: 100%;
  max-width: 300px;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .turnstile-container {
    margin: 0.75rem 0;
  }
  
  .turnstile-error {
    font-size: 0.8rem;
    padding: 0.4rem;
  }
}
</style>
